import { Link } from '@qbe/ads-core';
import {
  ContentConsumerProps,
  ContentConsumerType,
  useContent,
  useContentContext,
} from '@qbe/dfc-content';
import React, { JSX, ReactElement, ReactNode, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { TargetEnv, targetEnv } from '~/config';
import { setCoverInclusions } from '~/slices/coverInclusions';
import { Cover } from '~/types/cover';
import { formatContentKey } from '~/utils/sitecore';

interface Props extends Omit<ContentConsumerProps, 'context'> {}

/**
 * Converts all anchor (`<a>`) elements within the given JSX tree to ADS `Link` components.
 *
 * Recursively traverses the provided ReactNode, replacing any `<a>` tags with the ADS `Link` component.
 * In non-production environments, it warns if a link is missing the `data-track` attribute.
 *
 * @param jsx - The ReactNode to process and convert anchor tags.
 * @param _path - Optional path used for warning messages when `data-track` is missing.
 * @returns The processed ReactNode with all anchor tags replaced by ADS `Link` components.
 */
function convertToADSLinks(jsx: ReactNode, _path?: string): ReactNode {
  const processChildren = (children: ReactNode): ReactNode => {
    if (Array.isArray(children)) {
      return children.map(processChildren);
    }
    if (typeof children === 'object' && children !== null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const element = children as ReactElement<any>;
      if (element.type === 'a') {
        if (targetEnv !== TargetEnv.prod && !element.props['data-track']) {
          // console.warn(
          //   `Missing data-track attr: %c${path}%c of link of %c${element.props.children}`,
          //   'font-weight: bold',
          //   '',
          //   'font-weight: bold;text-decoration: underline'
          // );
        }
        return <Link key={element.key} {...element.props} />;
      }
      return React.cloneElement(element, {
        ...element.props,
        children: processChildren(element.props.children),
      });
    }

    return children;
  };

  return processChildren(jsx);
}

export const useAppContent = (props: Props = {}) => {
  const context = useContentContext();

  const dispatch = useDispatch();

  const { status, getTextContent, getJSXContent } = useContent({
    context,
    ...props,
  });

  const getJSX: ContentConsumerType['getJSXContent'] = (...args) => {
    const [itemPath, options] = args;

    const content = getJSXContent(itemPath, options);

    return convertToADSLinks(content, itemPath) as JSX.Element[];
  };

  const coverInclusions = context.data?.coverInclusions;
  if (status === 'ready' && !coverInclusions) console.error('coverInclusions is undefined');

  // Save coverInclusions to store when available and status is 'ready'
  useEffect(() => {
    if (status === 'ready' && coverInclusions) {
      dispatch(setCoverInclusions(coverInclusions));
    }
  }, [status, coverInclusions, dispatch]);

  const selectUiCover = (cover: Cover) => {
    if (!coverInclusions) return undefined;
    const uiCover = coverInclusions[formatContentKey(cover)];
    if (status === 'ready' && (!uiCover || !Object.keys(uiCover).length))
      throw Error('uiCover content is undefined or empty');
    return uiCover;
  };

  return {
    coverInclusions,
    selectUiCover,
    status,
    getTextContent,
    getJSXContent: getJSX,
  };
};
